/**
 * Shared TypeScript types for expert-review (questionnaire.types.ts).
 *
 * Belongs here: domain types.
 * Does not belong here: React components.
 */
export type QuestionInputType =
  | 'SINGLE_SELECT'
  | 'SINGLE_SELECT_CONFIRM'
  | 'AMOUNT'
  | 'PREFERENCE_INSIGHT'
  | 'MULTI_SELECT_GROUPS'

export interface QuestionInputValidations {
  minValue?: number
  maxValue?: number
  regex?: string
}

/** Screen A: backend tags plus recommended allocation band. */
export interface PreferenceIdealAllocation {
  absolute?: number | null
  min?: number | null
  max?: number | null
}

export interface PreferenceInsightMetadata {
  tags?: string[] | null
  ideal?: PreferenceIdealAllocation | null
}

export interface QuestionOptionFundItem {
  isin: string
  fundName: string
  currentValue?: number | null
  companyLogoUrl?: string | null
}

export interface QuestionInput {
  type: QuestionInputType
  title?: string | null
  placeholder?: string | null
  validations?: QuestionInputValidations | null
  inputDataString?: string | null
  inputDataNumber?: number | null
  selectedOptionIds?: string[] | null
  insight?: PreferenceInsightMetadata | null
}

export interface QuestionOptionReturnMetadata {
  bestCaseReturns?: number | null
  averageReturns?: number | null
  worstCaseReturns?: number | null
}

export interface QuestionOption {
  id: string
  displayText: string
  subText?: string
  order?: number
  expertRecommended?: boolean
  conditionalExpression?: string
  deprecated?: boolean
  portfolioShare?: number
  fundList?: QuestionOptionFundItem[]
  metadata?: QuestionOptionReturnMetadata | null
  reviewText?: string | null
}

export interface QuestionExpertRecommendation {
  displayText: string
  subText: string
  idealAllocationPercentage?: PreferenceIdealAllocation | null
}

export interface Question {
  id: string
  displayText: string
  subText?: string
  helperText?: string
  learnMoreUrl?: string
  portfolioShare?: number
  input: QuestionInput
  options: QuestionOption[]
  order?: number
  expertRecommendation?: QuestionExpertRecommendation
  conditionalExpression?: string
  answered?: boolean
  nextQuestionId?: string
  /** Preferences details: drives SEGMENT_INTRO insight screen only. */
  questionType?: string
  title?: string | null
  titleSubText?: string | null
  disclaimerText?: string | null
  segment?: string | null
  fundList?: QuestionOptionFundItem[]
}

export type QuestionAnswerValue = string | number | string[]
export type AllocationAnswersMap = Record<string, QuestionAnswerValue>
export type StrategyQuestionnaireAnswersMap = AllocationAnswersMap

/** Central status for allocation questionnaires (strategy, preference, stage progress). */
export enum QuestionnaireStatus {
  NOT_INITIATED = 'NOT_INITIATED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  /** Backend may return after submit — treat as done, same as COMPLETED (hub + seed). */
  SUBMITTED = 'SUBMITTED'
}

/** COMPLETED and SUBMITTED both mean answers are finished / safe to hydrate. */
export function isQuestionnaireStatusDone(
  status: QuestionnaireStatus | null | undefined
): boolean {
  return (
    status === QuestionnaireStatus.COMPLETED ||
    status === QuestionnaireStatus.SUBMITTED
  )
}

export interface SubmitQuestionInput {
  questionId: string
  optionId?: string[]
  inputValueString?: string
  inputValueDouble?: number
}

export enum StrategyQuestionnaireStageId {
  LifeStage = 'life_stage',
  RiskAssessment = 'risk_assessment',
  SipAllocation = 'sip'
}

export enum SipAllocationOptionId {
  Yes = 'yes',
  No = 'no'
}

export interface StrategyQuestionnaireStage {
  id: string
  displayText: string
  questions: Question[]
  status: QuestionnaireStatus
  order?: number
}

export interface ReviewSummaryCopyItem {
  displayText: string
  placeHolders?: { questionId: string }[]
}

export interface ReviewCopyEvaluation {
  conditionalExpression: string
  copies: ReviewSummaryCopyItem[]
}

export interface ReviewSummaryCopy {
  id: string
  label: string
  copyEvaluations: ReviewCopyEvaluation[]
}

export interface ReviewSummaryStage {
  id: string
  stageId: string
  label: string
  order?: number
  reviewCopies: ReviewSummaryCopy[]
}

export interface StrategyQuestionnairePayload {
  questionnaireId: string
  stages: StrategyQuestionnaireStage[]
  status: QuestionnaireStatus
  reviewSummary?: ReviewSummaryStage[]
}

export interface SubmitStrategyQuestionnaireApiRequest {
  questionnaireId: string
  questionInputs: SubmitQuestionInput[]
}

export interface StrategyQuestionnaireRiskProfileResponse {
  questionnaireId: string
  riskProfile: string
}

export enum ClearAnswersOnEditMode {
  CLEAR_ALL_AFTER_THIS = 'CLEAR_ALL_AFTER_THIS',
  CLEAR_CURRENT_STAGE_AFTER_THIS = 'CLEAR_CURRENT_STAGE_AFTER_THIS',
  CLEAR_ALL_STAGES = 'CLEAR_ALL_STAGES',
  CLEAR_CURRENT_STAGE = 'CLEAR_CURRENT_STAGE',
  NONE = 'NONE'
}

export interface ClearAnswersOptions {
  questionId?: string
  stageId?: string
}

export interface QuestionInputValidationsApi {
  minValue?: number
  maxValue?: number
  regex?: string | null
}

export interface QuestionOptionApi {
  id: string
  displayText: string
  subText: string | null
  conditionalExpression?: string | null
  isRecommended: boolean | null
  order: number
  portfolioShare?: number | null
  fundList?: QuestionOptionFundItem[] | null
  deprecated?: boolean | null
  metadata?: QuestionOptionReturnMetadata | null
  reviewText?: string | null
}

export interface QuestionInputMetadataApi {
  type: string
  validations: QuestionInputValidationsApi | null
  title: string | null
  placeholder: string | null
  inputDataString?: string | null
  inputDataNumber?: number | null
  selectedOptionIds?: string[] | null
  insight?: PreferenceInsightMetadata | null
  reviewText?: string | null
}

export interface PreferenceGroupRefApi {
  groupId: string
  groupName: string
}

export interface QuestionnaireQuestionApi {
  id: string
  preference?: string
  category?: string | null
  displayText: string
  subText: string | null
  helperText?: string | null
  learnMoreUrl?: string | null
  portfolioShare?: number | null
  input: QuestionInputMetadataApi
  conditionalExpression: string | null
  expertRecommendation: {
    displayText: string
    subText: string
    idealAllocationPercentage?: PreferenceIdealAllocation | null
  } | null
  options: QuestionOptionApi[]
  order: number
  questionType?: string | null
  title?: string | null
  titleSubText?: string | null
  disclaimerText?: string | null
  segment?: string | null
  answered?: boolean | null
  groups?: PreferenceGroupRefApi[] | null
  fundList?: QuestionOptionFundItem[] | null
}
